import { PaymentMethods } from "./application.dto";
import { Gateway } from "./wallet.transaction.dto";

export enum PaymentAttemptStatus {
  PENDING = "pending",
  AWAITING_APPROVAL = "awaiting_approval",
  SUCCEEDED = "succeeded",
  FAILED = "failed",
}

export enum PaymentComponent {
  ABCD = "ABCD",
  ABC = "ABC",
  BC = "BC",
  D_ONLY = "D_ONLY",
}

export enum JobName {
  PAYMENT_SLOT = "payment:slot",
  WATERFALL = "payment:waterfall",
  WATERFALL_STEP = "payment:waterfall-step",
  DISTRIBUTION = "payment:distribution",
  CREDITOR_OUTGOING = "payment:creditor-outgoing",
  NEXT_LEDGER = "payment:next-ledger",
  D_ONLY = "payment:d-only",
  PARTIAL_CREDITOR = "payment:partial-creditor",
}

export enum PaymentJobCategory {
  PAYMENT = "payment",
  DISTRIBUTION = "distribution",
  CREDITOR_OUTGOING = "creditor_outgoing",
}

export enum PaymentJobStatus {
  SCHEDULED = "scheduled",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
  LAPSED = "lapsed",
  CANCELLED = "cancelled",
  SUPERSEDED = "superseded",
}

export type PaymentAttemptDto = {
  _id: string;
  ledger_id: string;
  application_id: string;
  week: number;
  gateway: Gateway;
  payment_method_type: PaymentMethods;
  component: PaymentComponent;
  amount: number;
  status: PaymentAttemptStatus;
  external_transaction_id?: string;
  failure_reason?: string;
  attempted_at?: string;
  resolved_at?: string;
  meta?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export interface PaymentJobDto {
  _id?: string;
  agenda_job_id?: string;
  ledger_id: string;
  application_id: string;
  week: number;
  job_name: JobName;
  category: PaymentJobCategory;
  status: PaymentJobStatus;
  lapsed: boolean;
  scheduled_at: string;
  original_scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  failed_at?: string;
  failure_reason?: string;
  job_data: PaymentJobData;
  createdAt?: string;
  updatedAt?: string;
}

export interface BaseJobData {
  paymentJobId: string;
  ledgerId: string;
  applicationId: string;
  week: number;
  organisationId: string;
}

export interface SlotJobData extends BaseJobData {
  slot: number;
  walletId: string;
  reconWalletId: string;
}

export interface WaterfallJobData extends BaseJobData {
  walletId: string;
  reconWalletId: string;
}

export interface WaterfallStepJobData extends BaseJobData {
  walletId: string;
  reconWalletId: string;
  pmIndex: number;
  component: PaymentComponent;
  attemptId?: string;
}

export interface DistributionJobData extends BaseJobData {
  component: PaymentComponent;
  walletId: string;
  reconWalletId: string;
  orgProgramFeeWalletId?: string;
  orgLegalFeeWalletId?: string;
  orgServiceFeeWalletId?: string;
  creditorWallets?: Array<{
    creditorId: string;
    walletId: string;
    reconWalletId: string;
    amount: number;
    creditorName: string;
    priority: number;
  }>;
}

export interface CreditorOutgoingJobData extends BaseJobData {
  creditorId: string;
  creditorWalletId: string;
  creditorReconWalletId: string;
  rootWalletId: string;
  rootReconWalletId: string;
  amount: number;
  attemptCount: number;
  paynoteUserId?: string;
  paynoteSourceId?: string;
  isPartialCreditor: boolean;
  externalTransactionId?: string;
  manually_resolved?: boolean;
}

export interface DOnlyJobData extends BaseJobData {
  walletId: string;
  reconWalletId: string;
  pmIndex: number;
  attemptId?: string;
}

export interface PartialCreditorJobData extends BaseJobData {
  creditorId: string;
  creditorWalletId: string;
  creditorReconWalletId: string;
  rootWalletId: string;
  rootReconWalletId: string;
  amount: number;
  pmIndex: number;
  creditorName: string;
  priority: number;
  attemptId?: string;
}

export interface NextLedgerJobData extends BaseJobData {
  nextWeek: number;
}

export type PaymentJobData =
  | SlotJobData
  | WaterfallJobData
  | WaterfallStepJobData
  | DistributionJobData
  | CreditorOutgoingJobData
  | DOnlyJobData
  | PartialCreditorJobData
  | NextLedgerJobData;

export type SlotConfig = {
  slot: number;
  hour: number;
  minute: number;
  isWaterfall: boolean;
};

// ─── Fields NOT needed (PII / internal agenda noise) ──────────────────────
// agenda_job_id       – internal Agenda.js reference, no user value
// __v                 – Mongoose version key
// next_run_at         – Agenda internal scheduling field
// last_run_at         – Agenda internal field
// last_finished_at    – Agenda internal field
// locked_at           – Agenda concurrency lock, irrelevant in UI
// priority            – Agenda scheduling priority, opaque to users
// repeat_interval     – Agenda repeat config, not surfaced
// fail_reason (raw)   – Replace with clean error_message
// worker_id           – Internal worker identifier
